import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import {
  CONTEXT_GROUP_PATHS,
  firstIncompleteContextGroup,
  isContextComplete,
} from '../features/session/contextGroups';
import { QUESTION_IDS } from '../types/ids';

/** Ohne aktive Session führt geschützte Routen zu `/` (Abschnitt 8.2). */
export function useRequireSession(): boolean {
  const session = useAppStore((state) => state.session);
  const navigate = useNavigate();

  useEffect(() => {
    if (!session) void navigate('/', { replace: true });
  }, [session, navigate]);

  return !!session;
}

/** `/test/1` führt ohne vollständige Kontextantworten zum ersten fehlenden Kontextabschnitt. */
export function useRequireContextComplete(): boolean {
  const hasSession = useRequireSession();
  const context = useAppStore((state) => state.session?.context);
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasSession || !context) return;
    const missingGroup = firstIncompleteContextGroup(context);
    if (missingGroup) void navigate(CONTEXT_GROUP_PATHS[missingGroup], { replace: true });
  }, [hasSession, context, navigate]);

  return hasSession && !!context && isContextComplete(context);
}

/** Eine Testfrage ist nur bis zur ersten unbeantworteten Frage direkt aufrufbar (Abschnitt 8.2). */
export function useRequireQuestionAccessible(questionNumber: number): boolean {
  const contextReady = useRequireContextComplete();
  const answers = useAppStore((state) => state.session?.answers);
  const navigate = useNavigate();

  const firstUnansweredIndex = answers
    ? QUESTION_IDS.findIndex((id) => answers[id] === undefined)
    : 0;
  const maxAccessibleNumber =
    firstUnansweredIndex === -1 ? QUESTION_IDS.length : firstUnansweredIndex + 1;

  useEffect(() => {
    if (!contextReady) return;
    if (questionNumber > maxAccessibleNumber) {
      void navigate(`/test/${maxAccessibleNumber}`, { replace: true });
    }
  }, [contextReady, questionNumber, maxAccessibleNumber, navigate]);

  return contextReady && questionNumber <= maxAccessibleNumber;
}

/** `/pruefen` ist nur erreichbar, wenn alle 14 Fragen beantwortet wurden. */
export function useRequireAllAnswered(): boolean {
  const hasSession = useRequireSession();
  const answers = useAppStore((state) => state.session?.answers);
  const navigate = useNavigate();

  const allAnswered = !!answers && QUESTION_IDS.every((id) => answers[id] !== undefined);
  const firstUnansweredIndex = answers
    ? QUESTION_IDS.findIndex((id) => answers[id] === undefined)
    : 0;

  useEffect(() => {
    if (!hasSession) return;
    if (!allAnswered) {
      const target = firstUnansweredIndex === -1 ? 1 : firstUnansweredIndex + 1;
      void navigate(`/test/${target}`, { replace: true });
    }
  }, [hasSession, allAnswered, firstUnansweredIndex, navigate]);

  return hasSession && allAnswered;
}

/** `/ergebnis` benötigt ein Ergebnisobjekt oder vollständige Antworten, aus denen es berechnet wird. */
export function useRequireResult(): boolean {
  const hasSession = useRequireSession();
  const session = useAppStore((state) => state.session);
  const computeAndStoreResult = useAppStore((state) => state.computeAndStoreResult);
  const navigate = useNavigate();

  const allAnswered = !!session && QUESTION_IDS.every((id) => session.answers[id] !== undefined);

  useEffect(() => {
    if (!hasSession || !session) return;
    if (session.result) return;
    if (!allAnswered) {
      const firstUnansweredIndex = QUESTION_IDS.findIndex(
        (id) => session.answers[id] === undefined,
      );
      void navigate(`/test/${firstUnansweredIndex === -1 ? 1 : firstUnansweredIndex + 1}`, {
        replace: true,
      });
      return;
    }
    computeAndStoreResult();
  }, [hasSession, session, allAnswered, computeAndStoreResult, navigate]);

  return hasSession && !!session?.result;
}
